import { EVENT_LABELS, type EntryFull, type EntryRevision } from '@siteday/shared';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Cloud,
  CloudRain,
  CloudSnow,
  Download,
  Mail,
  Pencil,
  Sun,
  Wind,
  Wrench,
  X,
} from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';

import { entriesApi, errorMessage, isApiError } from '@/api';
import { BandPill } from '@/components/BandPill';
import { ErrorState, LoadingState, PageHeader } from '@/components/PageHeader';
import { EntryStatusPill } from '@/components/StatusPill';
import { Pill } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Card, CardBody, CardHeader, Table } from '@/components/ui/table';
import { useToast } from '@/components/ui/toast';
import { Tooltip } from '@/components/ui/tooltip';
import { useCompanyBasics, useImpersonation, useIsOffice } from '@/features/auth/session';
import { shortJob } from '@/features/dashboard/derive';
import { cn } from '@/lib/cn';
import { fmtDateLong, fmtTime, fmtWhen } from '@/lib/dates';
import { diffSnapshots, parseSnapshot, type Snapshot } from '@/lib/diff';
import { formatHours, formatMinutes, formatQuantity, plural, qty } from '@/lib/format';

import { ConflictView } from './ConflictView';
import { EmailReportDialog } from './EmailReportDialog';
import { EntryEditForm } from './EntryEditForm';
import { useApproveEntry, useEntry, useUnapproveEntry } from './useEntries';

/** W4 — day entry review: read view (PDF structure), lifecycle stepper, actions, revision history; conflict + edit modes. */
export function EntryPage() {
  const { id = '' } = useParams();
  const entry = useEntry(id);
  const { tz } = useCompanyBasics();
  const isOffice = useIsOffice();
  const { impersonating } = useImpersonation();
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [seed, setSeed] = useState<Snapshot | null>(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [confirmEdit, setConfirmEdit] = useState(false);
  const approve = useApproveEntry(id);
  const unapprove = useUnapproveEntry(id);
  const toast = useToast();

  if (entry.isPending) return <LoadingState />;
  if (entry.isError) return <ErrorState error={entry.error} onRetry={() => void entry.refetch()} />;
  const e = entry.data;
  const isReview = e.status === 'needs_review';
  // A Fuorix support session (read-only) sees the page but not the Edit affordance.
  const canEdit = isOffice && e.status !== 'draft' && !impersonating;
  // Correct after export (doc 10): an approved or synced day is editable; the edit keeps it approved and,
  // once exported, goes to QuickBooks as a compensating update on the next export run.
  const isApproved = e.status === 'approved' || e.status === 'synced';
  const approveDisabled = e.status === 'draft' || impersonating;

  const exitEdit = () => {
    setMode('view');
    setSeed(null);
  };

  return (
    <>
      <PageHeader
        title={
          <>
            <Link to={`/jobs/${e.jobId}`} className="hover:underline">
              {shortJob(e.jobName)}
            </Link>{' '}
            · {fmtDateLong(e.entryDate)}
          </>
        }
        sub={
          <span className="flex flex-wrap items-center gap-1.5">
            {e.foremanName} · finished {fmtTime(e.finishedAt, tz)} ·{' '}
            <EntryStatusPill status={e.status} /> <Pill>v{e.version}</Pill>
            {e.qboCorrectionPending ? (
              <Tooltip text="Edited after export. The next QuickBooks export run sends the corrected hours.">
                <Pill tone="warn" data-testid="correction-pending">
                  Correction pending export
                </Pill>
              </Tooltip>
            ) : null}
            {isReview ? <span>· the field and office versions disagree</span> : null}
          </span>
        }
        actions={
          mode === 'view' && !isReview ? (
            <>
              {canEdit ? (
                <Button
                  onClick={() => (isApproved ? setConfirmEdit(true) : setMode('edit'))}
                  data-testid="edit-entry"
                >
                  <Pencil /> Edit
                </Button>
              ) : null}
              <Button onClick={() => setEmailOpen(true)} disabled={!isOffice}>
                <Mail /> Email report
              </Button>
              <a href={entriesApi.reportPdfUrl(e.id)} target="_blank" rel="noreferrer">
                <Button>
                  <Download /> PDF
                </Button>
              </a>
              {isOffice ? (
                e.status === 'approved' ? (
                  <Tooltip
                    text={
                      e.exportedToQbo
                        ? 'Exported to QuickBooks — cannot be unapproved. Corrections create a new revision.'
                        : 'Back to finished'
                    }
                  >
                    <Button
                      variant="dark"
                      disabled={e.exportedToQbo}
                      loading={unapprove.isPending}
                      onClick={() =>
                        unapprove.mutate(undefined, {
                          onSuccess: () => toast.success('Unapproved'),
                          onError: (err) =>
                            toast.error(
                              isApiError(err) && err.code === 'ENTRY_EXPORTED'
                                ? err.message
                                : errorMessage(err),
                            ),
                        })
                      }
                    >
                      Unapprove
                    </Button>
                  </Tooltip>
                ) : (
                  <Button
                    variant="primary"
                    disabled={approveDisabled}
                    loading={approve.isPending}
                    onClick={() =>
                      approve.mutate(undefined, {
                        onSuccess: () => toast.success('Approved — ready to export'),
                        onError: (err) => toast.error(errorMessage(err)),
                      })
                    }
                    data-testid="approve-entry"
                  >
                    <Check /> Approve
                  </Button>
                )
              ) : null}
            </>
          ) : null
        }
      />

      {mode === 'view' && !isReview ? <Stepper entry={e} tz={tz} /> : null}

      {mode === 'edit' ? (
        <EntryEditForm
          key={`${e.version}-${seed ? 'seed' : 'live'}`}
          entry={e}
          seed={seed}
          onDone={exitEdit}
          onReload={() => void entry.refetch().then(exitEdit)}
        />
      ) : isReview ? (
        <ConflictView
          entry={e}
          tz={tz}
          onEditMerged={(s) => {
            setSeed(s);
            setMode('edit');
          }}
        />
      ) : (
        <ReadView entry={e} tz={tz} />
      )}

      {confirmEdit ? (
        <Dialog
          open
          onClose={() => setConfirmEdit(false)}
          title="Edit an approved day?"
          width="sm"
          footer={
            <>
              <Button onClick={() => setConfirmEdit(false)}>Cancel</Button>
              <Button
                variant="primary"
                data-testid="confirm-edit-approved"
                onClick={() => {
                  setConfirmEdit(false);
                  setMode('edit');
                }}
              >
                <Pencil /> Edit anyway
              </Button>
            </>
          }
        >
          <p className="m-0 text-[13px] text-ink" data-testid="approved-edit-warning">
            This day is approved. Your change will be sent to QuickBooks as a correction.
          </p>
          <p className="mt-2 mb-0 text-xs text-ink3">
            The day stays approved. The field version is kept; your reason is stored with the new
            revision{e.exportedToQbo ? ' and the exported hours are updated on the next export run' : ''}.
          </p>
        </Dialog>
      ) : null}

      <EmailReportDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        entryId={e.id}
        jobId={e.jobId}
        label={`${shortJob(e.jobName)} · ${fmtDateLong(e.entryDate)}`}
      />
    </>
  );
}

/** Finished → Synced → Reviewed → Approve → Exported (mockup lifecycle strip). */
function Stepper({ entry, tz }: { entry: EntryFull; tz: string }) {
  const officeRev = [...entry.revisions]
    .sort((a, b) => b.version - a.version)
    .find((r) => r.source === 'office');
  const finished = entry.status !== 'draft';
  const synced = finished && entry.syncVersion > 0;
  const reviewed = !!officeRev || entry.status === 'approved';
  const approved = entry.status === 'approved' || entry.status === 'synced';
  const exported = entry.exportedToQbo;
  const steps: Array<{ label: string; sub: string; state: 'done' | 'cur' | 'todo' }> = [
    {
      label: 'Finished',
      sub: finished
        ? `${entry.foremanName.split(' ')[0]} · ${fmtTime(entry.finishedAt, tz)}`
        : 'in progress on the phone',
      state: finished ? 'done' : 'cur',
    },
    {
      label: 'Synced',
      sub: synced ? fmtTime(entry.updatedAt, tz) : 'waiting for the phone',
      state: synced ? 'done' : finished ? 'cur' : 'todo',
    },
    {
      label: 'Reviewed',
      sub: officeRev
        ? `${officeRev.changedByName?.split(' ')[0] ?? 'office'} · ${fmtTime(officeRev.changedAt, tz)}`
        : approved
          ? 'at approval'
          : 'office edit optional',
      state: reviewed ? 'done' : synced ? 'cur' : 'todo',
    },
    {
      label: approved ? 'Approved' : 'Approve',
      sub: approved ? fmtTime(entry.approvedAt, tz) : 'unlocks export',
      state: approved ? 'done' : synced ? 'cur' : 'todo',
    },
    {
      label: 'Exported',
      sub: entry.qboCorrectionPending ? 'correction pending' : 'QuickBooks',
      state: exported ? 'done' : approved ? 'cur' : 'todo',
    },
  ];
  return (
    <div className="stepper" aria-label="Entry lifecycle">
      {steps.map((s, i) => (
        <span key={s.label} className="contents">
          {i > 0 ? <span className={cn('ln', steps[i - 1]!.state === 'done' && 'done')} /> : null}
          <div className={cn('step', s.state)}>
            <i>{s.state === 'done' ? '✓' : i + 1}</i>
            <span>
              {s.label}
              <small>{s.sub}</small>
            </span>
          </div>
        </span>
      ))}
    </div>
  );
}

const weatherIcon: Record<string, typeof Sun> = {
  clear: Sun,
  cloudy: Cloud,
  rain: CloudRain,
  snow: CloudSnow,
  wind: Wind,
};

function ReadView({ entry: e, tz }: { entry: EntryFull; tz: string }) {
  const crewTotal = e.crewLines.reduce((a, l) => a + Number(l.hours), 0);
  const eqTotal = e.equipmentLines.reduce((a, l) => a + Number(l.hours), 0);
  const WIcon = e.weatherCondition ? (weatherIcon[e.weatherCondition] ?? Sun) : null;
  const editedUsers = editedCrew(e);
  return (
    <div className="three">
      <div className="flex flex-col gap-4">
        <Card>
          <CardHeader title="Quantities">
            {WIcon ? (
              <span className="inline-flex items-center gap-1 text-ink3">
                <WIcon className="size-3.5" />{' '}
                {e.weatherCondition![0]!.toUpperCase() + e.weatherCondition!.slice(1)}
                {e.temperatureF !== null ? ` · ${e.temperatureF}°F` : ''}
              </span>
            ) : null}
          </CardHeader>
          <Table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Description</th>
                <th className="r">Today</th>
                <th>Unit</th>
                <th className="r">Plan / day</th>
                <th className="r">%</th>
                <th className="r">To-date / planned</th>
              </tr>
            </thead>
            <tbody>
              {e.quantityLines.map((q) => (
                <tr key={q.id} data-testid="qty-row">
                  <td>
                    <code>{q.code}</code>
                  </td>
                  <td>{q.description}</td>
                  <td className={cn('r', Number(q.quantity) === 0 && 'text-ink3')}>
                    {formatQuantity(q.quantity)}
                  </td>
                  <td>{q.unit}</td>
                  <td className="r">{qty(q.plannedQtyPerDay)}</td>
                  <td className="r">
                    <BandPill band={q.band} pct={q.pct} fallback="—" />
                  </td>
                  <td className="r">
                    {q.toDate !== undefined ? formatQuantity(q.toDate) : '—'} /{' '}
                    {qty(q.plannedTotal ?? null)}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>

        <div className="two">
          <Card>
            <CardHeader title="Crew">
              <span className="text-ink3">
                {e.crewLines.length} · {formatHours(crewTotal)} h
              </span>
            </CardHeader>
            <Table>
              <tbody>
                {e.crewLines.map((l) => (
                  <tr key={l.id}>
                    <td>
                      {l.userName}
                      {l.userId === e.foremanId ? <Pill className="ml-1">Foreman</Pill> : null}
                      {editedUsers.has(l.userId) ? (
                        <Pill tone="orange" className="ml-1">
                          edited
                        </Pill>
                      ) : null}
                    </td>
                    <td className="r">{formatHours(l.hours)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
          <Card>
            <CardHeader title="Equipment">
              <span className="text-ink3">
                {e.equipmentLines.length} · {formatHours(eqTotal)} h
              </span>
            </CardHeader>
            <Table>
              <tbody>
                {e.equipmentLines.length === 0 ? (
                  <tr>
                    <td className="text-ink3">No machines today</td>
                  </tr>
                ) : null}
                {e.equipmentLines.map((l) => (
                  <tr key={l.id}>
                    <td>
                      {l.equipmentName}
                      <span className="sub">{l.unitNumber ?? ''}</span>
                    </td>
                    <td className="r">{formatHours(l.hours)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <CardHeader title="Delays & extra work" className="border-t border-line2" />
            <CardBody className="flex flex-col gap-1.5 pt-2.5">
              {e.events.length === 0 ? <span className="text-ink3">None logged</span> : null}
              {e.events.map((ev) => (
                <div key={ev.id} className="flex items-center gap-1.5">
                  <Wrench className="size-3.5 text-ink3" />
                  <b>{EVENT_LABELS[ev.type]}</b> · {formatMinutes(ev.durationMinutes)}
                  {ev.description ? <span> — {ev.description}</span> : null}
                </div>
              ))}
            </CardBody>
          </Card>
        </div>

        <Card>
          <CardHeader title="Notes" />
          <CardBody className={cn('whitespace-pre-wrap', !e.notes && 'text-ink3')}>
            {e.notes ?? 'No notes'}
          </CardBody>
        </Card>

        <PhotosCard entry={e} tz={tz} />
      </div>

      <RevisionPanel revisions={e.revisions} approvedAt={e.approvedAt} tz={tz} />
    </div>
  );
}

/** Crew lines changed by an office revision (for the "edited" pill). */
function editedCrew(e: EntryFull): Set<string> {
  const out = new Set<string>();
  const sorted = [...e.revisions].sort((a, b) => a.version - b.version);
  for (let i = 1; i < sorted.length; i++) {
    const r = sorted[i]!;
    if (r.source !== 'office') continue;
    const prev = parseSnapshot(sorted[i - 1]!.snapshot);
    const cur = parseSnapshot(r.snapshot);
    if (!cur) continue;
    for (const d of diffSnapshots(prev, cur)) {
      if (d.section === 'Crew') {
        const line = e.crewLines.find((c) => c.userName === d.label);
        if (line) out.add(line.userId);
      }
    }
  }
  return out;
}

function PhotosCard({ entry: e, tz }: { entry: EntryFull; tz: string }) {
  const [open, setOpen] = useState<number | null>(null);
  const photos = e.photos;
  const current = open !== null ? photos[open] : undefined;
  return (
    <Card>
      <CardHeader title="Photos">
        <span className="text-ink3">
          {photos.length} · click to open
          {photos.some((p) => !p.uploadedAt)
            ? ` · ${photos.filter((p) => !p.uploadedAt).length} still uploading`
            : ''}
        </span>
      </CardHeader>
      <CardBody>
        {photos.length === 0 ? <span className="text-ink3">No photos</span> : null}
        <div className="pgrid">
          {photos.map((p, i) => (
            <button
              key={p.id}
              type="button"
              className="photo-tile"
              onClick={() => p.url && setOpen(i)}
              aria-label={p.caption ?? `Photo ${i + 1}`}
              disabled={!p.url}
            >
              {p.url ? (
                <img src={p.url} alt={p.caption ?? ''} loading="lazy" />
              ) : (
                <div className="flex h-full items-center justify-center text-xs text-ink3">
                  uploading…
                </div>
              )}
              <span className="cap">
                {p.caption ? `${p.caption} · ` : ''}
                {fmtTime(p.takenAt, tz)}
              </span>
            </button>
          ))}
        </div>
      </CardBody>
      {current ? (
        <Dialog
          open
          onClose={() => setOpen(null)}
          title={current.caption ?? 'Photo'}
          description={`${fmtWhen(current.takenAt, tz)} · ${current.width ?? '?'}×${current.height ?? '?'}`}
          width="xl"
        >
          <div className="relative">
            {current.url ? (
              <img
                src={current.url}
                alt={current.caption ?? ''}
                className="max-h-[70vh] w-full rounded-lg object-contain"
              />
            ) : null}
            <div className="mt-3 flex items-center gap-2">
              <Button
                size="sm"
                disabled={open === 0}
                onClick={() => setOpen((o) => (o ?? 1) - 1)}
                aria-label="Previous photo"
              >
                <ChevronLeft />
              </Button>
              <Button
                size="sm"
                disabled={open === photos.length - 1}
                onClick={() => setOpen((o) => (o ?? 0) + 1)}
                aria-label="Next photo"
              >
                <ChevronRight />
              </Button>
              <span className="text-xs text-ink3">
                {(open ?? 0) + 1} of {photos.length}
              </span>
              {current.url ? (
                <a
                  className="ml-auto"
                  href={current.url}
                  download={`${e.entryDate}-${current.id}.jpg`}
                >
                  <Button size="sm">
                    <Download /> Download
                  </Button>
                </a>
              ) : null}
            </div>
          </div>
        </Dialog>
      ) : null}
    </Card>
  );
}

const sourceTone = { office: 'info', foreman: 'orange', conflict: 'bad' } as const;

function RevisionPanel({
  revisions,
  approvedAt,
  tz,
}: {
  revisions: EntryRevision[];
  approvedAt: string | null;
  tz: string;
}) {
  const sorted = useMemo(() => [...revisions].sort((a, b) => b.version - a.version), [revisions]);
  const byVersion = new Map(sorted.map((r) => [r.version, r]));
  /** An office revision written after approval is a correction (doc 10): the day stayed approved. */
  const isCorrection = (r: EntryRevision) =>
    r.source === 'office' && approvedAt !== null && r.changedAt > approvedAt;
  return (
    <Card className="self-start" data-testid="revision-history">
      <CardHeader title="Revision history" />
      <CardBody className="flex flex-col">
        {sorted.map((r) => {
          const prev = byVersion.get(r.version - 1) ?? null;
          const cur = parseSnapshot(r.snapshot);
          const diff =
            cur && r.version > 1
              ? diffSnapshots(prev ? parseSnapshot(prev.snapshot) : null, cur)
              : [];
          return (
            <div key={r.id} className="border-b border-line2 py-2.5 last:border-b-0">
              <div className="flex flex-wrap items-center gap-2 font-bold">
                <span className="rounded bg-ink px-1.5 font-cond text-[11px] tracking-wide text-on-ink">
                  v{r.version}
                </span>
                {r.changedByName ?? 'Unknown'}
                <Pill tone={sourceTone[r.source]}>
                  {r.source === 'conflict' ? 'field · conflict' : r.source}
                </Pill>
                {isCorrection(r) ? (
                  <Pill tone="warn" data-testid="revision-correction">
                    correction after approval
                  </Pill>
                ) : null}
              </div>
              <div className="mt-0.5 text-xs text-ink3">
                {fmtWhen(r.changedAt, tz)}
                {r.reason ? (
                  <> · reason: “{r.reason}”</>
                ) : r.source === 'foreman' ? (
                  ' · Finish Day on the phone'
                ) : (
                  ''
                )}
              </div>
              {diff.length ? (
                <div className="rev-diff mt-1.5 flex flex-col gap-0.5 text-xs">
                  {diff.map((d, i) => (
                    <DiffLineView key={i} {...d} />
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
        <p className="mt-3 text-xs text-ink3">
          Edits never overwrite the field version. Each save keeps a full snapshot and regenerates
          the PDF.
        </p>
      </CardBody>
    </Card>
  );
}

function DiffLineView({
  section,
  label,
  before,
  after,
}: {
  section: string;
  label: string;
  before: string | null;
  after: string | null;
}) {
  const node: ReactNode = (
    <>
      {section} · {label} {before !== null ? <span className="del">{before}</span> : null}{' '}
      {after !== null ? <span className="ins">{after}</span> : <span className="del">removed</span>}
    </>
  );
  return <div>{node}</div>;
}

export function ClosePlaceholder() {
  return <X />;
}

export { plural };
